import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import loaderSvg from '../../../../../../assets/aica-loader.svg';
import { isEmpty } from '../../../../../../utils/utils';
import { AuditedRowSpec, AUDITED_UNIT_DIVISOR, AUDITED_UNIT_DEFAULT } from '../../auditedStatementLayout';
import styles from './AuditedStatementTable.module.scss';

type TableHeader = { name: string; key: string };
type TableRow = { classificationName?: string; [key: string]: any };

interface AuditedStatementTableProps {
    /** Figma-ordered row model for this statement (Balance Sheet / P&L / Cash Flow). */
    layout: AuditedRowSpec[];
    tableData: { headers?: TableHeader[]; rows?: TableRow[] };
    loading: boolean;
    /** Display unit for all figures ('Lakh' | 'Crore') — rescales each value at render time. Defaults to Lakh. */
    unit?: string;
    /** Shown instead of the table when the backend has no data at all for this statement. */
    emptyMessage: string;
}

const isBlank = (value: unknown) => value === undefined || value === null || value === '';

// Rescale a backend-formatted ₹ figure (e.g. "12,22,330", "-5,70,00,000") into the selected unit.
// INR (divisor 1, the default) shows the raw value untouched; Lakhs (÷1e5) / Crore (÷1e7) rescale
// and reformat with Indian grouping + 2 decimals. Non-numeric cells (blank, "—", "NA") pass through.
const rescaleValue = (value: unknown, unit: string): unknown => {
    const divisor = AUDITED_UNIT_DIVISOR[unit] ?? 1;
    if (divisor === 1) return value;
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '—') return value;
    const num = Number(trimmed.replace(/,/g, ''));
    if (!Number.isFinite(num)) return value;
    return (num / divisor).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const renderCellValue = (value: unknown) => {
    if (isBlank(value)) return '—';
    return value as React.ReactNode;
};

/**
 * Presentational table for ONE audited-financials statement, built to the RECUR-27010
 * redesign mock. Renders the fixed Figma row model (`layout`), joining backend values by
 * `classificationName`. Rows are non-clickable — no drill-down, no icons, no chevrons.
 * This REPLACES ClassifiedTable for the audited redesign surface only; ClassifiedTable
 * itself is untouched and still backs Provisional Financials.
 */
const AuditedStatementTable = ({ layout, tableData, loading, unit = AUDITED_UNIT_DEFAULT, emptyMessage }: AuditedStatementTableProps) => {
    const headers = tableData?.headers ?? [];
    const rows = (tableData?.rows ?? []).filter((row) => !isEmpty(row));
    const valueHeaders = headers.filter((header) => header.name !== 'Classification');
    const hasData = !isEmpty(headers) && valueHeaders.length > 0 && rows.length > 0;

    const rowsBySrc = rows.reduce<Record<string, TableRow>>((acc, row) => {
        if (row.classificationName) acc[row.classificationName] = row;
        return acc;
    }, {});

    // UAT RECUR-26713: on first render (and whenever the column set changes, e.g. a
    // Consolidated↔Standalone switch), scroll the statement horizontally to the most recent
    // (rightmost) year so the latest FY is in view on open rather than the oldest. The sticky
    // first column keeps the classification labels visible. rAF lets layout settle before we
    // read scrollWidth.
    const containerRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const el = containerRef.current;
        if (!hasData || !el) return undefined;
        const scrollToLatest = () => { el.scrollLeft = el.scrollWidth; };
        const raf = requestAnimationFrame(scrollToLatest); // after first paint
        const t = setTimeout(scrollToLatest, 250);         // re-apply after late reflow (webfont + provisional icon widen the last column)
        return () => { cancelAnimationFrame(raf); clearTimeout(t); };
    }, [hasData, valueHeaders.length]);

    if (loading) {
        return (
            <div className={styles.LoadingContainer}>
                <img className={styles.LoaderImg} src={loaderSvg} alt="Loading..." height="30%" width="30%" />
            </div>
        );
    }

    if (!hasData) {
        return <div className={styles.Empty}>{emptyMessage}</div>;
    }

    return (
        <div className={styles.TableContainer} ref={containerRef}>
            <table className={styles.Table}>
                <thead>
                    <tr>
                        <th className={styles.ClassificationHeaderCell}>Classification</th>
                        {valueHeaders.map((header) => (
                            <th key={header.key} className={styles.HeaderCell}>
                                {header.name}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {layout.map((row, rowIndex) => {
                        if (row.type === 'group') {
                            // Render a real first cell + empty value cells (NOT a single colSpan
                            // cell) so the label uses the same sticky-first-column treatment as
                            // every other row: the light-blue heading stays pinned left while the
                            // (empty) value area scrolls horizontally under the band.
                            return (
                                <tr key={rowIndex} className={styles.GroupRow}>
                                    <td>{row.label}</td>
                                    {valueHeaders.map((header) => (
                                        <td key={header.key} />
                                    ))}
                                </tr>
                            );
                        }

                        if (row.type === 'sub') {
                            return (
                                <tr key={rowIndex} className={styles.SubRow}>
                                    <td>{row.label}</td>
                                    {valueHeaders.map((header) => (
                                        <td key={header.key} />
                                    ))}
                                </tr>
                            );
                        }

                        const backendRow = row.src ? rowsBySrc[row.src] : undefined;
                        const noSource = !row.src;

                        return (
                            <tr
                                key={rowIndex}
                                className={clsx({
                                    [styles.SubtotalRow]: row.type === 'subtotal',
                                    [styles.GrandTotalRow]: row.type === 'subtotal' && row.grand,
                                    [styles.ItemRow]: row.type === 'item',
                                })}
                            >
                                <td>{row.label}</td>
                                {valueHeaders.map((header) => {
                                    const value = rescaleValue(backendRow ? backendRow[header.key] : undefined, unit);
                                    const isNegative = typeof value === 'string' && value.trim().startsWith('-');
                                    return (
                                        <td
                                            key={header.key}
                                            className={clsx({
                                                [styles.Negative]: isNegative,
                                                [styles.NoSource]: noSource,
                                            })}
                                        >
                                            {renderCellValue(value)}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default AuditedStatementTable;
